import { createContext } from 'react'
import { z } from 'zod'
import { DefaultChatTransport } from 'ai'
import type { RouterOutputs } from '@/trpc'

let text = z.string().or(z.number().or(z.boolean()).transform(String)) // a hand-typed ?q=123 arrives JSON-parsed
export let searchState = z.object({
  view: z.enum(['ask', 'tables', 'datasets', 'rules', 'tokens']).optional().catch(undefined),
  thread: z.string().optional().catch(undefined),
  question: z.string().optional().catch(undefined),
  dataset: z.string().optional().catch(undefined),
  delta: z.number().int().optional().catch(undefined),
  inspect: z.string().optional().catch(undefined),
  context: z.boolean().optional().catch(undefined),
  q: text.optional().catch(undefined),
  kind: z.enum(['all', 'table', 'column', 'definition', 'answer', 'value']).optional().catch(undefined),
  scope: z.array(z.string()).max(5).optional().catch(undefined),
  group: z.string().optional().catch(undefined),
  cluster: z.string().optional().catch(undefined),
  offset: z.number().int().min(0).optional().catch(undefined),
  tables: z.array(z.string()).optional().catch(undefined),
  table: z.string().optional().catch(undefined),
  tab: z.enum(['columns', 'memory']).optional().catch(undefined),
  column: z.string().optional().catch(undefined),
  value: text.optional().catch(undefined),
  drafts: z.boolean().optional().catch(undefined),
})
export type Search = z.output<typeof searchState>

export let closed = { table: undefined, tab: undefined, column: undefined, value: undefined, drafts: undefined }
// Another view opens without the previous one's inspector, context or delta marks.
export let toView = (view: NonNullable<Search['view']>, extra: Search = {}) => (prev: Search): Search =>
  ({ ...prev, view, delta: undefined, context: undefined, inspect: undefined, ...extra })
export let opened = (table: string, tab?: Search['tab']) => (prev: Search): Search =>
  ({ ...prev, ...closed, view: 'tables', table, tab, tables: prev.tables?.includes(table) ? prev.tables : [...prev.tables ?? [], table] })

export let assistantTransport = (schema: string, companyId: number | undefined, mode: 'chat' | 'probe') => new DefaultChatTransport({
  api: `${env.API_URL}/assistant/${schema}/${mode}`,
  credentials: 'include',
  headers: companyId != null ? { 'x-company-id': String(companyId) } : undefined,
})

export type WorkspacePanel =
  | { kind: 'answer' }
  | { kind: 'chat' }
  | { kind: 'source'; table: string }
  | { kind: 'publish'; key: string; question: string }
  | { kind: 'published'; result: RouterOutputs['layer']['publish'] }
  | { kind: 'excel'; dataset: RouterOutputs['layer']['datasets'][number] }

export let WorkspaceContext = createContext<{
  open: (panel: WorkspacePanel) => void
  ask: (question: string) => void
} | null>(null)
